/**
 * Zod schema for the user-profile fields a doctor can edit themselves (V2-E6.3,
 * HAN-66). Currently just the optional phone number used to SMS them a handover
 * code. The single source of truth for the phone format, shared so the web
 * profile form and the API DTO validate identically — the API's class-validator
 * DTO mirrors `PHONE_E164_PATTERN` and the same nullable-or-blank semantics.
 *
 * Kept dependency-light: only `zod`. No imports from the API or drizzle.
 */
import { z } from "zod";

/**
 * E.164 phone number: a leading `+`, a non-zero first digit, then up to 14 more
 * digits (15 total max). This is the format Twilio expects for the `to` field,
 * so storing numbers normalised to E.164 lets the SMS send path use them
 * verbatim. We deliberately do *not* try to validate national formats or
 * existence — only the wire format.
 */
export const PHONE_E164_PATTERN = /^\+[1-9]\d{1,14}$/;

/** A single E.164 number. Used inside the profile schema below. */
export const phoneE164Schema = z
  .string()
  .trim()
  .regex(PHONE_E164_PATTERN, "Enter a phone number in international format, e.g. +447700900123");

/**
 * Payload for `PATCH /auth/me` — update the caller's own profile. `phoneE164`
 * may be:
 *   - a valid E.164 number → set/replace the number;
 *   - `null` → clear the number ("I don't want SMS").
 *
 * An empty string from a cleared form input is coerced to `null` so the web
 * form can submit `""` to mean "remove". Omitting the field entirely is also
 * allowed (no change), but the web form always sends it.
 */
export const updateProfileSchema = z.object({
  phoneE164: z
    .union([phoneE164Schema, z.literal(""), z.null()])
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
